import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";

import { validateShelfSearch } from "./games-shelf/search";
import {
  EnvironmentLayout,
  IndexScreen,
  OrgLayout,
  ProjectLayout,
  RootLayout,
} from "./components/layouts";
import { LoadingRows } from "./components/shell";

/**
 * The route tree, written by hand rather than generated.
 *
 * @tanstack/router-plugin lags the router itself by two minor versions, and a
 * codegen step whose only output is this file is not worth a version skew in
 * the router that every screen depends on. The types are identical either way —
 * the plugin generates exactly this shape.
 *
 * The nesting mirrors what an id means. Everything under `/projects/$projectId`
 * needs a project; everything under `env/$envId` needs an environment. The
 * layouts at each level own the navigation for that scope, so a screen never
 * has to reason about where it sits.
 *
 * **Every screen is loaded on demand.** The console used to ship as one 628 kB
 * chunk, and roughly a third of it was screen code for destinations a given
 * session never opens — somebody checking a leaderboard downloaded the asset
 * uploader, the integrations editor and the game-loop map to do it. The route
 * is the right seam for that cut, because a route is exactly the unit a person
 * chooses.
 *
 * The layouts stay eager. They render on every navigation, so deferring them
 * buys nothing and costs a round trip on the first paint, which is the one
 * anybody notices.
 *
 * Each `import()` argument has to stay a literal string. A helper taking the
 * path as a variable would read better here and would leave the bundler unable
 * to see the module, which puts every screen back in the entry chunk with
 * nothing on screen to say it happened. `scripts/check-size.mjs` is what would
 * eventually say so.
 */

const rootRoute = createRootRoute({ component: RootLayout });

/**
 * `/` lands on the remembered studio, not inside a project — decisions/0038.
 *
 * It used to resolve the first project and redirect straight into it, which
 * is exactly the shape of the bug that decision names: a studio's games lived
 * nowhere but a dropdown, because the one screen everybody's `/` passed
 * through never stopped to show them. `IndexScreen` picks the studio a
 * membership can still stand behind and sends everyone there instead; the
 * shelf that lists its games is the studio's own home, one hop further in.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexScreen,
});

/**
 * The wish screen and the build page draw their own top bar, so neither has a
 * rail (`staticData.bare`, read by `AppShell`). `?how=` picks the way to build
 * and `?sheet=model` opens the add-a-model sheet.
 */
const wishRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wish/$orgId",
  staticData: { bare: true },
  validateSearch: (
    search: Record<string, unknown>,
  ): { how?: "djinn" | "claude" | "myself"; sheet?: "model" } => ({
    ...(search["how"] === "claude" || search["how"] === "myself" ? { how: search["how"] } : {}),
    ...(search["sheet"] === "model" ? { sheet: "model" as const } : {}),
  }),
  component: lazyRouteComponent(() => import("./routes/wish"), "WishScreen"),
});

const shapeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wish/$orgId/shape/$shapeId",
  staticData: { bare: true },
  component: lazyRouteComponent(() => import("./routes/shape"), "ShapeScreen"),
});

const buildRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wish/$orgId/builds/$buildId",
  staticData: { bare: true },
  component: lazyRouteComponent(() => import("./routes/build"), "BuildScreen"),
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: lazyRouteComponent(() => import("./routes/account"), "AccountScreen"),
});

const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/start",
  validateSearch: (
    search: Record<string, unknown>,
  ): { new?: 1; how?: "claude"; game?: string } => ({
    ...(String(search["new"]) === "1" || search["new"] === true ? { new: 1 as const } : {}),
    ...(search["how"] === "claude" ? { how: "claude" as const } : {}),
    ...(typeof search["game"] === "string" && search["game"] !== ""
      ? { game: search["game"] }
      : {}),
  }),
  component: lazyRouteComponent(() => import("./routes/start"), "StartScreen"),
});

/**
 * Redeeming an invitation link — the destination
 * `{ConsoleURL}/invitations/{token}` (`team.go` `acceptURL`) already points
 * at, and that nothing in the console has answered for until now (R3's Open
 * Question 1). A sibling of Account and Start rather than a child of
 * `orgRoute`: the person opening this link is not in the organisation yet,
 * so there is no `orgId` to nest it under. `RootLayout` gates sign-in ahead
 * of every route under `rootRoute`, which is what makes "sign in if needed,
 * then accept" the whole of this route's contract.
 */
const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitations/$token",
  component: lazyRouteComponent(
    () => import("./routes/invitation-accept"),
    "InvitationAcceptScreen",
  ),
});

/**
 * The studio scope — decisions/0038: the frame around everything a studio owns
 * rather than one of its games (its games shelf, Team, Plan, Network,
 * Settings). `OrgLayout` renders no navigation itself; the rail lives
 * in `sidebar.tsx`'s `Frame`, which reads `orgId` off the URL the same way it
 * already reads `projectId` for the project scope below.
 *
 * Nesting the routes that follow under one parent, rather than leaving
 * them flat children of the root with a repeated `/orgs/$orgId` prefix, is an
 * identity operation against the installed @tanstack/react-router 1.170.31: a
 * route's id is the parent id concatenated with its own path, so e.g.
 * `orgSettingsRoute`'s id is still `/orgs/$orgId/settings` whether it is a
 * child of `rootRoute` or of this route. Every existing
 * `useParams({ from: "/orgs/$orgId/settings" })`-style call in the route
 * screens below keeps resolving unchanged.
 */
const orgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orgs/$orgId",
  component: OrgLayout,
});

/**
 * The studio's home — decisions/0038's answer to "no screen lists a studio's
 * games": the games page. Sort, filter and search live in the URL, so Back and
 * refresh keep them.
 *
 * Its route id carries a trailing slash (`/orgs/$orgId/`), the way every index
 * route's does, while its `to`/`fullPath` normalise to `/orgs/$orgId`. A screen
 * reading params from here uses `from: "/orgs/$orgId"` — the parent layout's
 * own id — the way `overviewRoute` below already does at
 * `routes/overview.tsx:75` for the project index; hand-writing the
 * trailing-slash id is never the right spelling for this.
 */
const orgHomeRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/",
  validateSearch: validateShelfSearch,
  component: lazyRouteComponent(() => import("./routes/org-home"), "OrgHomeScreen"),
});

/**
 * Org-wide vendor connections, split off from the per-environment Integrations
 * screen the same day `scope` on `IntegrationEntry`/`IntegrationConnection`
 * became real (oasis commit `feat: org level and proj level integrations`).
 *
 * An `org`-scoped vendor (fal.ai, Ideogram, Stability AI) is one connection
 * shared by every project and environment in the org, so a route under
 * `/projects/$projectId/env/$envId` was the same kind of lie
 * `adNetworksRoute`'s move fixes: a screen that looked like it was describing
 * one environment's connection when a change there reached every one of them.
 * Reads and writes still go through an environment's own
 * `/v1/environments/{envId}/integrations*` endpoints underneath — oasis never
 * grew an `/v1/orgs/{orgId}/integrations` route of its own — this screen just
 * resolves one environment on the viewer's behalf instead of asking the URL
 * for one. See `routes/org-integrations.tsx`.
 */
const orgIntegrationsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/integrations",
  component: lazyRouteComponent(
    () => import("./routes/org-integrations"),
    "OrgIntegrationsScreen",
  ),
});

/**
 * Team is organisation-scoped, so it sits beside Account rather than inside a
 * project. A membership grants a role across a whole studio, and a route under
 * `/projects/$projectId` would suggest it could differ from one project to the
 * next.
 */
const teamRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/team",
  component: lazyRouteComponent(() => import("./routes/team"), "TeamScreen"),
});

/**
 * Studio settings sits beside Team and Plan, and for the same reason: what a
 * studio is called is a fact about the studio, not about one of its games.
 *
 * It is the frame for three tabs, each a child route so a tab has a URL:
 * Studio at `/settings`, Shared Loyalty at `/settings/loyalty`, AI at
 * `/settings/ai`. Adding children does not change this route's id, which is
 * still `/orgs/$orgId/settings` — the id every `useParams({ from })` in the
 * settings screens names.
 */
const orgSettingsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/settings",
  component: lazyRouteComponent(() => import("./routes/org-settings"), "OrgSettingsScreen"),
});

const orgSettingsStudioRoute = createRoute({
  getParentRoute: () => orgSettingsRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./routes/org-settings"), "OrgSettingsStudioTab"),
});

const orgSettingsLoyaltyRoute = createRoute({
  getParentRoute: () => orgSettingsRoute,
  path: "/loyalty",
  component: lazyRouteComponent(() => import("./routes/org-settings"), "OrgSettingsLoyaltyTab"),
});

/** Djinn's studio page, now the AI tab: emails and the Watch switch, for every game in the studio. */
const orgSettingsAiRoute = createRoute({
  getParentRoute: () => orgSettingsRoute,
  path: "/ai",
  component: lazyRouteComponent(() => import("./routes/org-djinn"), "OrgDjinnTab"),
});

/** Where the studio Djinn page used to be, kept for old links and emails. */
const orgDjinnRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/djinn",
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: "/orgs/$orgId/settings/ai",
      params: { orgId: params.orgId },
      replace: true,
    });
  },
});

/** Plan is organisation-scoped for the same reason Team is. */
const planRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/plan",
  validateSearch: (search: Record<string, unknown>): { sheet?: "overage" | "packs" } =>
    search.sheet === "overage" || search.sheet === "packs" ? { sheet: search.sheet } : {},
  component: lazyRouteComponent(() => import("./routes/plan"), "PlanScreen"),
});

/** Choosing a plan: every plan on sale, each at the price paid, GST included. */
const planChooseRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/plan/choose",
  validateSearch: (search: Record<string, unknown>): { sheet?: "gst" } =>
    search.sheet === "gst" ? { sheet: "gst" } : {},
  component: lazyRouteComponent(() => import("./routes/plan-choose"), "ChoosePlanScreen"),
});

/** Moving to one plan: billing details, review, pay, done. */
const planCheckoutRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/plan/checkout/$planCode",
  validateSearch: (search: Record<string, unknown>): { step?: "gst" | "review" } =>
    search.step === "gst" || search.step === "review" ? { step: search.step } : {},
  component: lazyRouteComponent(() => import("./routes/plan-checkout"), "CheckoutScreen"),
});

/** Every invoice, and the billing details they are made out to. */
const planInvoicesRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/plan/invoices",
  validateSearch: (search: Record<string, unknown>): { invoice?: string; sheet?: "gst" } => ({
    ...(typeof search.invoice === "string" && search.invoice
      ? { invoice: search.invoice }
      : {}),
    ...(search.sheet === "gst" ? { sheet: "gst" as const } : {}),
  }),
  component: lazyRouteComponent(() => import("./routes/plan-invoices"), "InvoicesScreen"),
});

/**
 * Usage — players onboarded and credits spent, organisation-scoped for
 * Plan's reason. Plan says what was bought; this says what was used, and it
 * has the sidebar row and the credit chip's link.
 */
const usageRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/usage",
  component: lazyRouteComponent(() => import("./routes/usage"), "UsageScreen"),
});

/**
 * Every credit charge. Reached only from Usage's "See every charge" link — no
 * sidebar row. It is evidence for the numbers Usage already shows, not a
 * destination somebody browses to on its own.
 */
const usageCreditsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/usage/credits",
  component: lazyRouteComponent(() => import("./routes/usage-credits"), "CreditChargesScreen"),
});

/**
 * Network sits beside Plan, and for a sharper version of Plan's reason.
 *
 * A membership is an organisation's, and whether it may hold one is decided
 * entirely by the organisation's plan — only a paid plan may join. A route under
 * `/projects/$projectId` would suggest a studio could be in the network for one
 * game and not another, which is not a thing that can be true.
 */
const networkRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/network",
  component: lazyRouteComponent(() => import("./routes/network"), "NetworkScreen"),
});

/**
 * The acceptor's own screen: what taking in somebody else's coin did for this
 * studio. Beside the shelf for the same reasons — org-scoped, gated at the
 * server rather than by the route.
 */
const networkAcceptanceRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/network/acceptance",
  component: lazyRouteComponent(
    () => import("./routes/network-acceptance"),
    "NetworkAcceptanceScreen",
  ),
});

// ---------------------------------------------------------------- project scope

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  component: ProjectLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./routes/overview"), "OverviewScreen"),
});

/** Game settings (C2): the name, the slug it cannot change, where it runs and its code. */
const gameSettingsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/settings",
  component: lazyRouteComponent(() => import("./routes/game-settings"), "GameSettingsScreen"),
});

const environmentsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/environments",
  component: lazyRouteComponent(() => import("./routes/environments"), "EnvironmentsScreen"),
});

/**
 * Project-scoped, not a section of Integrations, and both for reasons that
 * changed together. `ad_network_connection` moved to being keyed by
 * `project_id` (oasis migration `..._ad_network_connection_project_scope`):
 * an ad network's app id is bought once per game, and dev, staging and
 * production already shared it in practice — a per-environment screen was
 * the lie, not the fix. It stays its own destination rather than folding into
 * Integrations for the same reason it always was: a studio arriving here is
 * asking "why is nobody getting their coins", a different question in a
 * different mood from "which vendors does this project use", and it needs
 * the callback URL, the four checks and the revenue reconciliation on one
 * page. See `routes/ad-networks.tsx` for how a callback URL — still
 * genuinely per environment — is shown without one in this route's path.
 */
const adNetworksRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/ad-networks",
  component: lazyRouteComponent(() => import("./routes/ad-networks"), "AdNetworksScreen"),
});

/**
 * Operate › Djinn: the ledger, four short lists. `#r-<id>` opens the sheet at that card and
 * `?snooze=<finding id>` (the link in a finding's email) snoozes it. The old home stays in
 * the tree, unrouted, until R13 deletes it.
 */
const djinnLedgerRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/djinn",
  validateSearch: (search: Record<string, unknown>): { snooze?: string } =>
    typeof search["snooze"] === "string" && search["snooze"] !== ""
      ? { snooze: search["snooze"] }
      : {},
  component: lazyRouteComponent(() => import("./routes/djinn-ledger"), "DjinnLedgerScreen"),
});

const releasesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/releases",
  component: lazyRouteComponent(() => import("./routes/releases"), "ReleasesScreen"),
});

/**
 * One event type, on its own page.
 *
 * The document you cannot write a rule without had no destination: the editor
 * existed only as step one of the rule wizard, so it was reachable only by
 * starting to write a rule and could never be found again afterwards.
 *
 * The segment must be spelled `$name` — `EventTypeScreen` reads it with
 * `useParams({ strict: false })` and takes `params.name`, so any other spelling
 * renders the "Nothing here declares that" empty state for every event type.
 */
/**
 * Every rule, on its own page — the list `RuleScreen`'s own "Back goes to the
 * list" comment used to have no destination for. Rules used to surface only
 * grouped under a mechanic's board or a "loose rules" strip under an event;
 * this names every one of them regardless, and is the one door onto a rule
 * with neither a mechanic nor an event already chosen for it.
 *
 * Nested at `/rules/rules` rather than bare `/rules`, which now belongs to
 * `rulesHubRoute` below — one of Game Rules' sections, symmetric with
 * `/rules/events`, rather than the odd one out squatting on its parent's own
 * URL.
 */
const rulesListRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/rules/rules",
  component: lazyRouteComponent(() => import("./routes/rules-list"), "RulesScreen"),
});

/**
 * `/rules` on its own names no section — Events and Rules are the two the
 * tab strip (`rules/game-rules-header.tsx`) still shows, so bare `/rules`
 * redirects into the first of them rather than rendering a hub page of its
 * own. Used to be a real landing page (`routes/rules-hub.tsx`, since
 * deleted) with a card grid to all four sections; its draft-moved banner and
 * "What changed" link moved into the shared header instead of staying
 * behind on a page nothing links to any more.
 */
const rulesHubRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/rules",
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: "/projects/$projectId/rules/events",
      params: { projectId: params.projectId },
      replace: true,
    });
  },
});

const eventTypeRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/rules/events/$name",
  component: lazyRouteComponent(() => import("./routes/event-types"), "EventTypeScreen"),
});

/**
 * The vocabulary itself — every event, what each carries, and the try-it
 * panel. An event is what your game says; a mechanic is what you build. The
 * describe and try tools lived as states of the Game Rules list, which is a
 * gallery of mechanics now.
 */
const eventVocabularyRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/rules/events",
  component: lazyRouteComponent(() => import("./routes/event-vocabulary"), "EventsScreen"),
});

/**
 * One rule, on its own page — S6, the sheet that replaced the four-step wizard.
 *
 * `$key` is the rule's document key. A new rule arrives with a key that does not
 * exist yet, and the move sheet reaches it with `?mechanic=&to=&from=` so the
 * MOVE clause is written before the sheet opens: that is what makes the move
 * context on the sheet rather than a field on it.
 */
const ruleRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/rules/rules/r/$key",
  component: lazyRouteComponent(() => import("./routes/rule"), "RuleScreen"),
});

/**
 * What changed — S9, and the destination the draft-has-moved strip on S1 points
 * at. Deletes and renames first, because those are what an agent's work can take
 * away from you.
 */
const changesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/rules/changes",
  component: lazyRouteComponent(() => import("./routes/changes"), "ChangesScreen"),
});

/**
 * Something isn't paying — S12.
 *
 * Project-scoped rather than environment-scoped on purpose: what it diagnoses is
 * a document in the working draft, and *which* environment to check it against is
 * the shell's existing switcher rather than a second question in the URL.
 */
const notPayingRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/rules/diagnose",
  component: lazyRouteComponent(() => import("./routes/not-paying"), "NotPayingScreen"),
});

/**
 * Stats and Leaderboards were one screen called Progression, and the name was
 * the module's rather than the reader's.
 *
 * `progression` is what the platform calls the three document kinds — it is the
 * directory the documents live in and the prefix on every one of their paths.
 * Nobody outside this repository calls a stat and a leaderboard "progression".
 * A game developer looking for the place to add a leaderboard scans the sidebar
 * for the word "leaderboard", does not find it, and concludes the platform does
 * not have them: decision 0012's failure exactly, our vocabulary on a screen
 * read by people who do not share it. The sidebar's own comment says the
 * grouping is "by job rather than by module", and this was the one entry that
 * was not.
 *
 * So two destinations, named after the two things people already know, and two
 * URLs that say the same. They are separate routes rather than tabs on one
 * because they are separate jobs: a stat is written once when the game learns
 * to count something, and a leaderboard is opened again every time somebody
 * asks who is winning.
 *
 * **Game modes sit with Leaderboards.** A game mode is what a run is and where
 * its result goes — its editor is a list of leaderboards with checkboxes beside
 * them, and it refuses to save naming one that does not exist. It is authored
 * in the same sitting as the board it feeds, and Stats knows nothing about it.
 * A third destination for a document most projects never write would be a
 * sidebar entry that is empty for nearly everyone.
 */
const statsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/stats",
  component: lazyRouteComponent(() => import("./routes/stats"), "StatsScreen"),
});

const leaderboardsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/leaderboards",
  component: lazyRouteComponent(() => import("./routes/leaderboards"), "LeaderboardsScreen"),
});

/**
 * The URL the split replaced, kept and pointed at Stats.
 *
 * `/progression` has been linked in tickets and pasted into chats, and a 404 on
 * a link somebody trusted costs more than a route that outlives the screen it
 * named. Renaming a destination is our decision; making everyone who wrote the
 * old name down pay for it is not.
 *
 * Stats rather than Leaderboards, because it is the half that stands on its
 * own. Every leaderboard is an ordering over a stat, so somebody who wanted
 * leaderboards arrives having passed through what they are built on and finds
 * them on the next line of the sidebar; landing on Leaderboards instead sends
 * the other reader looking backwards for something they never saw. `replace`,
 * so Back goes where they came from rather than into the redirect again.
 */
const progressionRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/progression",
  beforeLoad: ({ params }) => {
    // `redirect()` returns a Response, not an Error, and throwing it is the
    // router's own documented way to say "not here, there" before a screen
    // renders. The rule is right about application code and wrong about this.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: "/projects/$projectId/stats",
      params: { projectId: params.projectId },
      replace: true,
    });
  },
});

/**
 * Shops — `decisions/0035`'s one commerce surface: every card a studio makes,
 * and the platform's own card on the same wall. This replaced `/store`, the
 * flat shelf, and there is deliberately no redirect from it: v2 is
 * staging-only, nobody holds the old URL the way `/progression` was held, and
 * an alias would leave the word naming a screen it has just stopped naming —
 * the `/usage` argument, made once already a few routes down.
 */
const shopsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/shops",
  component: lazyRouteComponent(() => import("./routes/shops"), "ShopsScreen"),
});

/**
 * New shop — its own URL, so a half-finished create survives a refresh and
 * the wall is off screen while it is open. Registered before
 * `/shops/s/$shopKey`; a static segment must not be readable as a key.
 */
const shopNewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/shops/new",
  component: lazyRouteComponent(() => import("./routes/shop-new"), "NewShopScreen"),
});

/**
 * The Magicweave Shop — the platform's card, read-only, and the studio's
 * support view of what its players bought from it. A static segment rather
 * than `s/magicweave`, because it is not one of the studio's own documents:
 * the key is reserved at publish, so nothing a studio writes can collide.
 */
const shopMagicweaveRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/shops/magicweave",
  component: lazyRouteComponent(
    () => import("./routes/shop-magicweave"),
    "MagicweaveShopScreen",
  ),
});

/**
 * One shop — the `s/` segment follows `/rules/rules/r/$key`: the card is
 * linkable, pasteable into a ticket, and Back means "the wall" instead of
 * "leave the module".
 */
const shopRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/shops/s/$shopKey",
  component: lazyRouteComponent(() => import("./routes/shop"), "ShopScreen"),
});

/**
 * Inventory is its own destination and not a section of Shops.
 *
 * The two are one sentence apart and that sentence is the whole argument: a
 * shop decides what a player *pays* for something, and this decides what the
 * something *is*. An item exists whether or not anything sells it — a rule on
 * Game Rules can hand one over — so filing it under the shop would say an item
 * is a thing you buy, which is the one thing it is not necessarily.
 *
 * Project-scoped, like everything else that writes the draft. An item is a
 * document in a release, and which release an environment is serving is the
 * question the badge on each row answers.
 *
 * Items and Gacha are two subroutes of one layout rather than two flat
 * siblings — unlike Stats/Leaderboards below, which the router deliberately
 * keeps flat. The two are one destination because they answer one question
 * together ("what can a player end up holding, and how"), so the layout owns
 * a tab strip and each child owns its own screen. `/inventory` itself is an
 * index redirect to `/inventory/items`, the same shape `progressionRoute`
 * uses for `/stats`.
 */
const inventoryRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/inventory",
  component: lazyRouteComponent(() => import("./routes/inventory"), "InventoryLayout"),
});

const inventoryIndexRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: "/projects/$projectId/inventory/items",
      params: { projectId: params.projectId },
      replace: true,
    });
  },
});

const inventoryItemsRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "/items",
  component: lazyRouteComponent(
    () => import("./routes/inventory-items"),
    "InventoryItemsScreen",
  ),
});

const inventoryGachaRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "/gacha",
  component: lazyRouteComponent(
    () => import("./routes/inventory-gacha"),
    "InventoryGachaScreen",
  ),
});

// ------------------------------------------------------------ environment scope

const envRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/env/$envId",
  component: EnvironmentLayout,
});

const playersRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/players",
  component: lazyRouteComponent(() => import("./routes/players"), "PlayersScreen"),
});

const playerRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/players/$playerId",
  component: lazyRouteComponent(() => import("./routes/player"), "PlayerScreen"),
});

/**
 * Rooms — decisions/0034's console surface: what this environment has anything
 * to show for right now, unioned from presence and `modules/roomstate`.
 */
/**
 * One component for both room routes, not one lazy wrapper each. Selecting a room in the split is a
 * change of the URL from `/rooms` to `/rooms/$room`; with the same component on both, React keeps
 * the screen mounted across it and the list's search and scroll stay where the reader left them.
 */
const RoomsSplit = lazyRouteComponent(() => import("./routes/rooms"), "RoomsScreen");

const roomsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/rooms",
  component: RoomsSplit,
});

/**
 * One room, named in the URL for the reason `mechanicRoute`'s and
 * `ruleRoute`'s own `$key` are: a room a support conversation is about should
 * survive a refresh and be pasteable into a ticket. Registered after
 * `roomsRoute` — a static segment must not be readable as a room name.
 */
const roomRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/rooms/$room",
  component: RoomsSplit,
});

const eventsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/events",
  component: lazyRouteComponent(() => import("./routes/events"), "EventsScreen"),
});

const requestsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/requests",
  component: lazyRouteComponent(() => import("./routes/requests"), "RequestsScreen"),
});

const analyticsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/analytics",
  component: lazyRouteComponent(() => import("./routes/analytics"), "AnalyticsScreen"),
});

/**
 * One request, reached from the list or from a trace id pasted into the URL.
 *
 * Pasting one is a first-class way in, not a fallback: every event and every
 * timeline entry carries the id, so the path from "why didn't this player get
 * their rule" to the request that answers it is a copy and a paste.
 */
const requestRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/requests/$traceId",
  component: lazyRouteComponent(() => import("./routes/requests"), "RequestScreen"),
});

const economyRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/economy",
  component: lazyRouteComponent(() => import("./routes/economy"), "EconomyScreen"),
});

/**
 * Economy health is Operate's question, and the Economy screen is not it.
 *
 * Economy answers "what is this environment serving"; health answers "is what it
 * is serving working" — where currency comes from, where it goes, and whether
 * one rule is quietly paying for everything. Two questions, so two screens.
 *
 * It sits under an environment and reads the whole project, which is not a
 * contradiction: the comparison it exists for needs every environment's figures
 * over one window, and the environment in the path is the one it leads with.
 */
const economyHealthRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/economy-health",
  component: lazyRouteComponent(() => import("./routes/economy-health"), "EconomyHealthScreen"),
});

const connectRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/connect",
  component: lazyRouteComponent(() => import("./routes/connect"), "ConnectScreen"),
});

/**
 * Its own destination rather than a section of Integrations, for the same
 * reason `adNetworksRoute` above is: a studio arriving here is asking "can I
 * reach my players on their phone", a different question in a different mood
 * from "which vendors does this environment use". It reads the same vendor
 * catalog as Integrations, filtered to `kind === "push_sender"` rather than
 * backed by anything of its own.
 */
const notificationsRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/notifications",
  component: lazyRouteComponent(() => import("./routes/notifications"), "NotificationsScreen"),
});

/**
 * Player sign-in sits under an environment, and that is the safety property
 * rather than a filing decision.
 *
 * A provider's client ids are per environment because v1's were per project,
 * which meant a token minted for a development build verified in production.
 * A project-level route here would be a claim that one set of ids serves both,
 * and the consequence of that claim is somebody's test credentials signing a
 * stranger in to the live game.
 */
const signInRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/sign-in",
  component: lazyRouteComponent(() => import("./routes/sign-in"), "SignInScreen"),
});

/**
 * Per environment for Player sign-in's reason: a development build must not
 * sign players into the production Firebase project. Reads the vendor catalog
 * filtered to `kind === "auth_token_minter"`.
 */
const firebaseSignInRoute = createRoute({
  getParentRoute: () => envRoute,
  path: "/firebase-sign-in",
  component: lazyRouteComponent(
    () => import("./routes/firebase-sign-in"),
    "FirebaseSignInScreen",
  ),
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  accountRoute,
  startRoute,
  wishRoute,
  shapeRoute,
  buildRoute,
  invitationAcceptRoute,
  orgRoute.addChildren([
    orgHomeRoute,
    orgIntegrationsRoute,
    teamRoute,
    orgSettingsRoute.addChildren([
      orgSettingsStudioRoute,
      orgSettingsLoyaltyRoute,
      orgSettingsAiRoute,
    ]),
    orgDjinnRoute,
    planRoute,
    planChooseRoute,
    planCheckoutRoute,
    planInvoicesRoute,
    usageRoute,
    usageCreditsRoute,
    networkRoute,
    networkAcceptanceRoute,
  ]),
  projectRoute.addChildren([
    overviewRoute,
    rulesHubRoute,
    statsRoute,
    leaderboardsRoute,
    progressionRoute,
    inventoryRoute.addChildren([inventoryIndexRoute, inventoryItemsRoute, inventoryGachaRoute]),
    shopsRoute,
    shopNewRoute,
    shopMagicweaveRoute,
    shopRoute,
    environmentsRoute,
    gameSettingsRoute,
    adNetworksRoute,
    djinnLedgerRoute,
    releasesRoute,
    rulesListRoute,
    eventTypeRoute,
    eventVocabularyRoute,
    ruleRoute,
    changesRoute,
    notPayingRoute,
    envRoute.addChildren([
      playersRoute,
      playerRoute,
      roomsRoute,
      roomRoute,
      eventsRoute,
      requestsRoute,
      analyticsRoute,
      requestRoute,
      economyRoute,
      economyHealthRoute,
      connectRoute,
      signInRoute,
      firebaseSignInRoute,
      notificationsRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,

  /**
   * Fetch a screen when somebody looks like they are about to open it.
   *
   * Loading screens on demand put a network round trip between the click and
   * the new screen, which was not there before. Preloading on hover or focus —
   * after a 50 ms delay, so a cursor crossing the sidebar does not fetch
   * everything it passes — spends that trip while the person is still deciding,
   * so the common path is as immediate as it was when everything shipped up
   * front. A screen fetched and never opened costs a few kilobytes of cache.
   */
  defaultPreload: "intent",

  /**
   * What a slow fetch looks like, for the times preloading did not get there.
   *
   * The router keeps the previous screen on the page until the next one is
   * ready, so a navigation is never half-drawn. That is right, and it means a
   * slow connection shows a click that appears to do nothing. This is the
   * answer to that, and it deliberately waits: TanStack's default holds it back
   * for a second and then shows it for at least half of one, so a fast load
   * stays seamless and only a genuinely slow one gets a skeleton — rather than
   * a flicker on every navigation, which reads as the console being unsure.
   */
  defaultPendingComponent: () => <LoadingRows rows={3} />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
